/**
 * A custom element that lets the buyer opt in to marking their order as a
 * gift. Unchecked (the default): no note, no "Gift" attribute is sent —
 * the order is treated as not a gift. Checked: reveals an optional message
 * field, and both the note and a `Gift: Yes` cart attribute are synced so
 * the order is unambiguously flagged even if no message is typed.
 *
 * Deliberately framework-free (plain HTMLElement, no @theme/component refs
 * system) so it behaves the same regardless of whether this markup arrives
 * via a full page load or a cart-fragment re-render/morph.
 */
class ZahavGiftNote extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('[data-gift-checkbox]');
    this.field = this.querySelector('[data-gift-field]');
    this.textarea = this.querySelector('[data-gift-textarea]');

    if (!this.checkbox || !this.field || !this.textarea) return;
    if (this.dataset.wired === 'true') return;
    this.dataset.wired = 'true';

    this.checkbox.addEventListener('change', () => this.handleToggle());
    this.textarea.addEventListener('input', () => this.handleInput());
  }

  handleToggle() {
    const isGift = this.checkbox.checked;
    this.field.hidden = !isGift;
    if (!isGift) {
      this.textarea.value = '';
    }
    this.sync();
  }

  handleInput() {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this.sync(), 200);
  }

  async sync() {
    const isGift = this.checkbox.checked;
    const note = isGift ? this.textarea.value : '';
    const updateUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) || '/cart/update.js';

    if (this._activeFetch) {
      this._activeFetch.abort();
    }
    const controller = new AbortController();
    this._activeFetch = controller;

    try {
      await fetch(updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          note,
          attributes: { Gift: isGift ? 'Yes' : '' },
        }),
        signal: controller.signal,
      });
    } catch (error) {
      // Ignore aborted/failed background syncs.
    } finally {
      this._activeFetch = null;
    }
  }
}

if (!customElements.get('zahav-gift-note')) {
  customElements.define('zahav-gift-note', ZahavGiftNote);
}
